import type { CoursesAdminUserTagDto } from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	AdminListUserTagsInput,
	AdminListUserTagsOutput,
	AdminAddUserTagInput,
	AdminAddUserTagOutput,
	AdminRemoveUserTagInput,
	AdminRemoveUserTagOutput,
	AdminListAllUserTagsOutput,
} from '../../types/user-tags.types';

const TAG = 'courses:user-tags' as const;
/** Cache id of the all-users tag list, next to the per-user ids. */
const ALL_USERS = 'ALL';

const invalidatesUserTags = (_result: unknown, _error: unknown, { userId }: { userId: string }) => [
	{ type: TAG, id: userId },
	{ type: TAG, id: ALL_USERS },
	'courses:admin' as const,
	'courses:courses' as const,
	'courses:dashboard' as const,
];

export const userTagsEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		adminListUserTags: builder.query<AdminListUserTagsOutput, AdminListUserTagsInput>({
			query: ({ userId }) => ({ url: `courses/admin/users/${encodeURIComponent(userId)}/tags` }),
			providesTags: (_result, _error, { userId }) => [{ type: TAG, id: userId }],
		}),
		adminAddUserTag: builder.mutation<AdminAddUserTagOutput, AdminAddUserTagInput>({
			query: ({ userId, ...body }) => ({
				url: `courses/admin/users/${encodeURIComponent(userId)}/tags`,
				method: 'POST',
				body: body satisfies Wire<CoursesAdminUserTagDto>,
			}),
			invalidatesTags: invalidatesUserTags,
		}),
		adminRemoveUserTag: builder.mutation<AdminRemoveUserTagOutput, AdminRemoveUserTagInput>({
			query: ({ userId, ...body }) => ({
				url: `courses/admin/users/${encodeURIComponent(userId)}/tags`,
				method: 'DELETE',
				body: body satisfies Wire<CoursesAdminUserTagDto>,
			}),
			invalidatesTags: invalidatesUserTags,
		}),
		adminListAllUserTags: builder.query<AdminListAllUserTagsOutput, void>({
			query: () => ({ url: 'courses/admin/users/tags' }),
			providesTags: [{ type: TAG, id: ALL_USERS }],
		}),
	}),
});

export const {
	useAdminListUserTagsQuery,
	useLazyAdminListUserTagsQuery,
	useAdminAddUserTagMutation,
	useAdminRemoveUserTagMutation,
	useAdminListAllUserTagsQuery,
	useLazyAdminListAllUserTagsQuery,
} = userTagsEndpoints;
